import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ChevronDown, Loader2, MapPin, Sparkles } from "lucide-react";
import { levelLabel, providerInitials, typeLabel } from "@/lib/opportunities";

const distanceChip = (programme) => (
  programme.online ? "Online" : programme.distance_km != null ? `About ${programme.distance_km} km away` : programme.location
);

export const IntakeStartsLine = ({ summary, testId }) => {
  if (!summary) return null;
  if (summary.rolling) {
    return <p className="mt-1 text-sm font-semibold text-cobalt" data-testid={testId}>Rolling intake — start anytime</p>;
  }
  if (summary.available?.length > 1) {
    return (
      <p className="mt-1 text-sm font-semibold text-cobalt" data-testid={testId}>
        Available starts: {summary.available.map((intake) => intake.intake_label).join(" · ")}
      </p>
    );
  }
  if (summary.next_available) {
    return (
      <p className="mt-1 text-sm font-semibold text-cobalt" data-testid={testId}>
        {summary.closed_note && <span className="mr-1 font-bold text-amber-700">{summary.closed_note}</span>}
        Next available start: {summary.next_available.intake_label}
      </p>
    );
  }
  if (summary.planned?.length) {
    return <p className="mt-1 text-sm font-semibold text-slatey" data-testid={testId}>Next planned start: {summary.planned[0].intake_label} — applications not yet open</p>;
  }
  return <p className="mt-1 text-sm font-semibold text-slatey" data-testid={testId}>Start dates to be confirmed</p>;
};

export const ProvidersStep = ({ preview, loading, error, onChooseOpportunity }) => {
  const [expanded, setExpanded] = useState(null);
  const locationContext = preview?.location_context || {};
  const providers = preview?.providers || [];

  return (
    <div data-testid="journey-providers">
      <p className="inline-flex items-center gap-2 rounded-full bg-cobalt/10 px-3 py-1 text-xs font-bold text-cobalt"><Sparkles className="h-4 w-4" /> Indicative guidance</p>
      <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-ink">Providers who can offer what you’re looking for</h2>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-slatey">Explore a provider to see its relevant programmes, then choose the opportunity you want OpenDegree to check.</p>

      {locationContext.explanation && (
        <div className="mt-6 flex items-start gap-3 rounded-3xl border border-sky-200 bg-sky-50 p-5" data-testid="journey-location-context">
          <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-cobalt" />
          <p className="text-sm leading-6 text-slatey">{locationContext.explanation}</p>
        </div>
      )}

      {loading && <p className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-slatey"><Loader2 className="h-4 w-4 animate-spin" /> Finding relevant providers…</p>}
      {error && <p className="mt-6 rounded-2xl bg-rose-50 p-3 text-sm font-semibold text-rose-700" data-testid="journey-providers-error">{error}</p>}

      {!loading && !error && (
        <div className="mt-6 space-y-4">
          {providers.map((provider) => (
            <article key={provider.provider_id} data-testid={`journey-provider-${provider.provider_id}`} className="rounded-3xl border border-slate-200 bg-surface p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-cobalt/10 font-display font-bold text-cobalt">{providerInitials(provider.provider)}</div>
                  <div>
                    <h3 className="font-display text-xl font-bold text-ink">{provider.provider}</h3>
                    <p className="mt-1 text-sm text-slatey">{provider.programme_count} relevant programme{provider.programme_count === 1 ? "" : "s"} · {provider.best_relevance}% match</p>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs font-bold text-slatey">
                      {provider.subject_areas.slice(0, 4).map((area) => <span key={area} className="rounded-full bg-white px-3 py-1">{area}</span>)}
                      {provider.online && <span className="rounded-full bg-white px-3 py-1">Online available</span>}
                      {provider.min_distance_km != null && <span className="rounded-full bg-white px-3 py-1">From about {provider.min_distance_km} km</span>}
                    </div>
                  </div>
                </div>
                <button onClick={() => setExpanded(expanded === provider.provider_id ? null : provider.provider_id)} data-testid={`journey-provider-toggle-${provider.provider_id}`}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-ink">
                  {expanded === provider.provider_id ? "Hide programmes" : "View programmes"} <ChevronDown className={`h-4 w-4 transition-transform ${expanded === provider.provider_id ? "rotate-180" : ""}`} />
                </button>
              </div>

              {expanded === provider.provider_id && (
                <div className="mt-5 space-y-3 border-t border-slate-200 pt-5" data-testid={`journey-provider-programmes-${provider.provider_id}`}>
                  {provider.programmes.map((programme) => (
                    <div key={programme.slug} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white p-4" data-testid={`journey-programme-${programme.slug}`}>
                      <div className="min-w-0">
                        <p className="font-display text-lg font-bold text-ink">{programme.title}</p>
                        <p className="mt-1 text-sm text-slatey">{typeLabel(programme.opportunity_type)} · {levelLabel(programme.qualification_level)} · {programme.mode || "Flexible"} · <span data-testid={`journey-distance-${programme.slug}`}>{distanceChip(programme)}</span></p>
                        <IntakeStartsLine summary={programme.intakes_summary} testId={`journey-starts-${programme.slug}`} />
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Link to={`/opportunities/${programme.slug}`} data-testid={`journey-discover-${programme.slug}`} className="rounded-full border border-slate-300 px-4 py-2.5 text-sm font-bold text-ink">Discover</Link>
                        <button onClick={() => onChooseOpportunity(programme.slug)} data-testid={`journey-check-${programme.slug}`} className="rounded-full bg-cobalt px-4 py-2.5 text-sm font-bold text-white">Check eligibility</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </article>
          ))}
          {providers.length === 0 && (
            <div className="rounded-3xl border border-slate-200 bg-surface p-6 text-sm leading-6 text-slatey" data-testid="journey-providers-empty">
              <Building2 className="mb-2 h-5 w-5 text-cobalt" /> We couldn’t match a provider yet. Try a broader subject area or the “Something else” option.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
